import {
  matchesWaterbody,
  matchesPetCharges,
  matchesPetChargesAmount,
  matchesDogsInPool,
  matchesTrekTrail,
  matchesMinRating,
  matchesFilterCity,
  matchesNearMe,
} from './stayFilterUtils';
import {
  stayMatchesSecondaryFilters,
  hasActiveSecondaryFilters,
} from './listingAttributeMatch';
import { getStayBookableMinPrice } from './stayComingSoon';

/** Minimum discounted room price for a stay (used for budget filter + sorting). */
export function getStayMinPrice(stay) {
  const min = getStayBookableMinPrice(stay);
  return min != null ? min : 0;
}

/** True when the stay can be booked for the current search (dates/guests). */
export function isStayRequestAvailable(stay) {
  return stay?.canAccommodateRequest !== false;
}

/** Price key for sorting — missing/zero prices sink to the bottom within a tier. */
export function getStaySortPrice(stay) {
  const min = getStayMinPrice(stay);
  return min > 0 ? min : Number.POSITIVE_INFINITY;
}

function compareAvailabilityFirst(a, b) {
  const aOk = isStayRequestAvailable(a);
  const bOk = isStayRequestAvailable(b);
  if (aOk === bOk) return 0;
  return aOk ? -1 : 1;
}

/**
 * Sort stays for search results. Available stays always rank above unavailable ones;
 * price sorts treat missing prices as last (not ₹0).
 */
export function sortStays(staysToSort, sortOption) {
  if (!staysToSort?.length) return staysToSort || [];

  const sortedStays = [...staysToSort];

  switch (sortOption) {
    case 'newest':
      return sortedStays.sort((a, b) => {
        const av = compareAvailabilityFirst(a, b);
        if (av !== 0) return av;
        const dateA = new Date(a.createdAt || a.version || 0);
        const dateB = new Date(b.createdAt || b.version || 0);
        return dateB - dateA;
      });

    case 'cheapest':
      return sortedStays.sort((a, b) => {
        const av = compareAvailabilityFirst(a, b);
        if (av !== 0) return av;
        return getStaySortPrice(a) - getStaySortPrice(b);
      });

    case 'expensive':
      return sortedStays.sort((a, b) => {
        const av = compareAvailabilityFirst(a, b);
        if (av !== 0) return av;
        const priceA = getStaySortPrice(a);
        const priceB = getStaySortPrice(b);
        if (priceA === priceB) return 0;
        if (priceA === Number.POSITIVE_INFINITY) return 1;
        if (priceB === Number.POSITIVE_INFINITY) return -1;
        return priceB - priceA;
      });

    case 'popular':
      return sortedStays.sort((a, b) => {
        const av = compareAvailabilityFirst(a, b);
        if (av !== 0) return av;
        return (b.rating || 0) - (a.rating || 0);
      });

    case 'alphabetical':
      return sortedStays.sort((a, b) => {
        const av = compareAvailabilityFirst(a, b);
        if (av !== 0) return av;
        const nameA = (a.title || a.name || '').toLowerCase();
        const nameB = (b.title || b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

    default:
      return sortedStays.sort(compareAvailabilityFirst);
  }
}

/**
 * Client-side advanced filters — same rules as public search results.
 * @param {object[]} stays
 * @param {{ primary: object, secondary: object }} advancedFilters
 */
export function applyStayAdvancedFilters(stays, advancedFilters) {
  if (!stays || stays.length === 0) return stays || [];
  let filtered = [...stays];
  const primary = advancedFilters?.primary || {};
  const secondary = advancedFilters?.secondary || {};

  if (primary.budget > 0) {
    const maxPrice = primary.budget * 1000;
    filtered = filtered.filter((stay) => {
      const minPrice = getStayMinPrice(stay);
      if (!minPrice || minPrice <= 0) return true;
      return minPrice <= maxPrice;
    });
  }

  if (primary.minRating > 0) {
    filtered = filtered.filter((stay) => matchesMinRating(stay, primary.minRating));
  }

  if (primary.waterbody && primary.waterbody.length > 0) {
    filtered = filtered.filter((stay) => matchesWaterbody(stay, primary.waterbody));
  }

  if (primary.petCharges) {
    filtered = filtered.filter((stay) => matchesPetCharges(stay, primary.petCharges));
  }

  if (primary.petCharges === 'Paid' && (primary.petChargesAmount || 0) > 0) {
    filtered = filtered.filter((stay) =>
      matchesPetChargesAmount(stay, primary.petChargesAmount)
    );
  }

  if (primary.dogsInPool) {
    filtered = filtered.filter((stay) => matchesDogsInPool(stay, primary.dogsInPool));
  }

  if (primary.trekTrail && primary.trekTrail.length > 0) {
    filtered = filtered.filter((stay) => matchesTrekTrail(stay, primary.trekTrail));
  }

  if (primary.filterCity && String(primary.filterCity).trim()) {
    filtered = filtered.filter((stay) => matchesFilterCity(stay, primary.filterCity));
  }

  if (primary.nearMe) {
    filtered = filtered.filter((stay) => matchesNearMe(stay, primary.nearMe));
  }

  if (hasActiveSecondaryFilters(secondary)) {
    filtered = filtered.filter((stay) => stayMatchesSecondaryFilters(stay, secondary));
  }

  return filtered;
}
